import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Install prompt hook (Android/desktop Chromium).
 *
 * Install prompt logic:
 * - The browser fires `beforeinstallprompt` when the PWA is installable.
 * - We **preventDefault** to stop the mini-infobar and store the event.
 * - UI can show an "Install App" button when `isInstallAvailable` is true.
 * - When the user clicks, call `promptInstall()` to trigger the browser prompt.
 *
 * Note:
 * - iOS Safari does **not** support `beforeinstallprompt`. Use a separate iOS banner
 *   with "Share → Add to Home Screen" guidance instead.
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hasInstalled, setHasInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setHasInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const isInstallAvailable = useMemo(() => !!deferredPrompt && !hasInstalled, [deferredPrompt, hasInstalled]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'dismissed' };

    // The stored event can only be used once.
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice;
  }, [deferredPrompt]);

  return { isInstallAvailable, promptInstall, hasInstalled };
}

